<template>
  <div class="profile-list">
    <div class="help-text">
      <template v-if="profilePersonsRef.length">
        <template v-if="isSelfRef">
          Profiles of living or recently living people that you manage.
        </template>
        <template v-else>
          Profiles of living or recently living people that are managed by this user.
        </template>
      </template>
      <template v-else>
        <template v-if="isSelfRef">You do not manage any profiles.</template>
        <template v-else>This user does not manage any profiles that are visible to you.</template>
      </template>
    </div>
    <div>
      <button type="button" v-if="isSelfRef" class="btn btn-primary btn-sm" @click="showAddProfileModal">
        <PlusIcon size="1.2x" class="mb-1"/>
        Add a profile
      </button>
    </div>
    <ul class="list-unstyled">
      <li v-for="p in profilePersonsRef" :key="p.id">
        <ProfileCard :person-id="p.id" show-profile-type/>
      </li>
    </ul>
    <ProfileAddModal ref="profileAddModalRef"/>
  </div>
</template>

<style lang="scss" scoped>
.profile-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

button.nav-link {
  position: relative;
}

.owner-area {
  margin-bottom: 2rem;

  .owner {
    margin-left: 1rem;
  }
}

ul {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { useDataGroupMemberStore } from '@/gp/DataGroupMemberStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useTokenManager } from '@/auth/TokenManager'
import { DataGroupType, GroupMemberRole } from '@/gp/GroupAdminModel'
import { isDefined } from '@/util/TypeScriptUtil'
import { Person } from '@/rd/ResearchDataModel'
import { LoadMode } from '@/util/AsyncData'
import { usePageTitle } from '@/util/AppUtil'
import { PlusIcon } from '@zhuowenli/vue-feather-icons'
import ProfileCard from '@/manage/ProfileCard.vue'
import ProfileAddModal from './ProfileAddModal.vue'

const props = defineProps<{
  userId?: string
}>()

const profileAddModalRef = ref<InstanceType<typeof ProfileAddModal>>()

const userStore = useUserStore()
const dataGroupStore = useDataGroupStore()
const dataGroupMemberStore = useDataGroupMemberStore()
const personStore = usePersonStore()
const tokenManager = useTokenManager()

const userRef = computed(() => userStore.getAsyncUser(userIdRef.value, LoadMode.EnsureLoaded)?.data)
const profilePersonsRef = computed(() => {
  const ownerMembers = dataGroupMemberStore.getMembersForUsers([userIdRef.value], LoadMode.EnsureLoaded)
    .filter(dm => dm.role == GroupMemberRole.Owner && dm.groupType == DataGroupType.Profile)

  const personIds = dataGroupStore.getLoadedGroupList(ownerMembers.map(dm => dm.dataGroupId))
    .map(pg => pg.startItemId)

  // load persons so we can get them all at once AND sort them
  return personStore.getAsyncPersonList(personIds, LoadMode.EnsureLoaded)
    .map(a => a.data).filter(isDefined)
    .sort(Person.compareDisplayName)
})

const userIdRef = computed(() => props.userId ?? tokenManager.current.userId)
const isSelfRef = computed(() => userIdRef.value == tokenManager.current.userId)

usePageTitle("Managed Profiles", () => isSelfRef.value ? "" : userRef.value?.displayName)

function showAddProfileModal() {
  profileAddModalRef.value?.open()
}
</script>
